import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Galerija: jedna slika po kadru, object-fit: cover (ispuna bez praznog prostora).
 * Pomeranje trake u pikselima (ResizeObserver).
 */
export function ImageSlider({
  images,
  auto = true,
  interval = 6000
}) {
  const viewportRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const length = images?.length ?? 0;

  const measure = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = Math.round(r.width);
    const h = Math.round(r.height);
    if (w > 0 && h > 0) {
      setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    }
  }, []);

  useLayoutEffect(() => {
    measure();
    const id = requestAnimationFrame(() => measure());
    return () => cancelAnimationFrame(id);
  }, [measure]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", measure);
    };
  }, [measure]);

  useEffect(() => {
    if (!auto || length <= 1) return;
    timeoutRef.current && clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setCurrent((prev) => (prev + 1) % length);
    }, interval);
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [current, auto, interval, length]);

  const goTo = useCallback(
    (index) => {
      if (!length) return;
      setCurrent(((index % length) + length) % length);
    },
    [length]
  );

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;
    const delta = touchEndX.current - touchStartX.current;
    const threshold = 48;
    if (delta > threshold) goTo(current - 1);
    else if (delta < -threshold) goTo(current + 1);
    touchStartX.current = null;
    touchEndX.current = null;
  };

  if (!length) return null;

  const { w, h } = dims;
  const trackWidthPx = w * length;
  const offsetPx = w * current;

  return (
    <div className="image-carousel image-carousel--cover">
      <div
        ref={viewportRef}
        className="image-carousel__viewport"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="image-carousel__track"
          style={{
            width: trackWidthPx > 0 ? `${trackWidthPx}px` : "100%",
            height: h > 0 ? `${h}px` : "100%",
            transform: w > 0 ? `translate3d(-${offsetPx}px, 0, 0)` : "none"
          }}
        >
          {images.map((img, index) => (
            <figure
              key={`${img.src}-${index}`}
              className="image-carousel__slide"
              style={
                w > 0 && h > 0
                  ? { width: `${w}px`, height: `${h}px`, flex: "0 0 auto" }
                  : { flex: "1 0 100%", minHeight: "260px" }
              }
              aria-hidden={index !== current}
            >
              <div className="image-carousel__frame">
                <img
                  src={img.src}
                  alt={img.alt}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  draggable={false}
                  style={
                    img.objectPosition
                      ? { objectPosition: img.objectPosition }
                      : undefined
                  }
                />
              </div>
              {img.caption ? (
                <figcaption className="image-carousel__caption image-carousel__caption--overlay">
                  {img.caption}
                </figcaption>
              ) : null}
            </figure>
          ))}
        </div>

        {length > 1 && (
          <>
            <button
              type="button"
              className="image-carousel__arrow image-carousel__arrow--prev"
              aria-label="Prethodna slika"
              onClick={() => goTo(current - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className="image-carousel__arrow image-carousel__arrow--next"
              aria-label="Sledeća slika"
              onClick={() => goTo(current + 1)}
            >
              ›
            </button>
          </>
        )}
      </div>

      {length > 1 && (
        <div className="image-carousel__dots" role="tablist" aria-label="Izbor slike">
          {images.map((img, index) => (
            <button
              key={`dot-${img.src}-${index}`}
              type="button"
              role="tab"
              aria-selected={index === current}
              className={`image-carousel__dot ${index === current ? "is-active" : ""}`}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
